import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useTranslations } from '@/hooks/useTranslations';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { QrCode, Package, Scan, MapPin, Clock, CheckCircle, LogOut, Plus, ScanLine, TrendingUp } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import QRScanner from '@/components/QRScanner';
import { ClientQRScanner } from '@/components/relay/ClientQRScanner';
import { formatPriceWithCurrency } from '@/utils/currency';
import { useCurrency } from '@/hooks/useCurrency';
import { usePersistentTabState } from '@/hooks/usePersistentTabState';

interface Order {
  id: string;
  status: string;
  amount_cents: number;
  currency: string;
  created_at: string;
  shelf_location: string | null;
  product: {
    title: string;
    images: string[];
  };
  client: {
    full_name: string | null;
    phone: string | null;
  };
  pickup_tokens: {
    qr_svg: string | null;
    otp_code_hash: string | null;
  }[] | null;
}

const RelayDashboard = () => {
  const { user, profile, signOut } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslations();
  const navigate = useNavigate();
  const { displayCurrency, convertPrice } = useCurrency();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [scanDialog, setScanDialog] = useState(false);
  const [showClientScanner, setShowClientScanner] = useState(false);
  const [otpDialog, setOtpDialog] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [shelfLocation, setShelfLocation] = useState('');
  const [scanMode, setScanMode] = useState<'qr' | 'manual'>('qr');
  const [depositScanMode, setDepositScanMode] = useState<'qr' | 'manual' | 'select' | 'shelf'>('qr');
  const [scannedOrderId, setScannedOrderId] = useState<string>('');
  const [scannedOtpCode, setScannedOtpCode] = useState<string>('');
  const [selectedDepositOrderId, setSelectedDepositOrderId] = useState<string>('');
  const [relayId, setRelayId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = usePersistentTabState(
    'relay-dashboard:activeTab',
    'awaiting',
    ['awaiting', 'available', 'completed']
  );

  useEffect(() => {
    const currentRole = profile?.system_role || profile?.role;
    const hasRelayManagerAccess = profile?.has_vendor_management_access === true;
    if (!user || (currentRole !== 'relay' && currentRole !== 'super_admin' && currentRole !== 'admin' && !hasRelayManagerAccess)) {
      navigate('/');
      return;
    }
    fetchRelayData();
  }, [user, profile, navigate]);

  useEffect(() => {
    if (!relayId) return;

    // Subscribe to real-time order updates
    const channelConfig = relayId === 'all' 
      ? {
          event: '*' as const,
          schema: 'public',
          table: 'orders'
        }
      : {
          event: '*' as const,
          schema: 'public',
          table: 'orders',
          filter: `relay_id=eq.${relayId}`
        };

    const channel = supabase
      .channel('relay-orders-changes')
      .on('postgres_changes', channelConfig, () => {
        fetchOrders();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [relayId]);

  const fetchRelayData = async () => {
    if (!user) return;

    try {
      setLoading(true);

      const currentRole = profile?.system_role || profile?.role;
      const hasRelayManagerAccess = profile?.has_vendor_management_access === true;

      // Les admins et gestionnaires de magasin ont une vue globale des commandes relais.
      if (currentRole === 'super_admin' || currentRole === 'admin' || hasRelayManagerAccess) {
        setRelayId('all');
        await fetchOrders('all');
        return;
      }

      // Récupérer le relay_id de l'utilisateur
      const { data: relayData, error: relayError } = await supabase
        .from('profiles')
        .select('relay_id')
        .eq('id', user.id)
        .single();

      if (relayError) throw relayError;

      const userRelayId = relayData?.relay_id;
      if (!userRelayId) {
        toast({
          title: "Erreur",
          description: "Aucun point relais associé à votre compte",
          variant: "destructive"
        });
        return;
      }

      setRelayId(userRelayId);
      await fetchOrders(userRelayId);

    } catch (error) {
      console.error('Error fetching relay data:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les données",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async (relayIdParam?: string) => {
    const targetRelayId = relayIdParam || relayId;
    if (!targetRelayId) return;

    try {
      let query = supabase
        .from('orders')
        .select(`
          *,
          product:products(title, images),
          client:profiles!orders_client_id_fkey(full_name, phone),
          pickup_tokens(qr_svg, otp_code_hash)
        `)
        .in('status', ['PAID', 'AVAILABLE_FOR_PICKUP', 'PICKED_UP'])
        .order('created_at', { ascending: false });

      // Si pas super_admin (pas 'all'), filtrer par relay_id
      if (targetRelayId !== 'all') {
        query = query.eq('relay_id', targetRelayId);
      } else {
        // Pour super_admin, ne montrer que les commandes avec un relay_id
        query = query.not('relay_id', 'is', null);
      }

      const { data, error } = await query;

      if (error) throw error;
      
      // Ensure pickup_tokens is always an array
      const ordersWithArrayTokens = (data || []).map(order => ({
        ...order,
        pickup_tokens: Array.isArray(order.pickup_tokens) ? order.pickup_tokens : (order.pickup_tokens ? [order.pickup_tokens] : [])
      }));
      
      setOrders(ordersWithArrayTokens);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const handleConfirmDropOff = async (orderId: string, location: string) => {
    try {
      const { error } = await supabase
        .from('orders')
        .update({
          status: 'AVAILABLE_FOR_PICKUP',
          shelf_location: location,
          available_at: new Date().toISOString()
        })
        .eq('id', orderId);

      if (error) throw error;

      // Log the action
      await supabase
        .from('audit_logs')
        .insert({
          actor_id: user?.id,
          action: 'CONFIRM_DROP_OFF',
          order_id: orderId,
          relay_id: null, // Would be set to actual relay ID
          meta: { shelf_location: location }
        });

      toast({ title: "Dépôt confirmé avec succès" });
      fetchOrders();
      setScanDialog(false);
      setShelfLocation('');
    } catch (error: any) {
      toast({ 
        title: "Erreur", 
        description: error.message,
        variant: "destructive" 
      });
    }
  };

  const handleConfirmPickup = async (orderId: string, otpCode: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('validate-pickup-token', {
        body: { orderId, otpCode }
      });

      // Extract message from error context if the function returned non-2xx
      if (error) {
        let msg = "Impossible de valider le code";
        try {
          const ctx = (error as any)?.context;
          if (ctx) {
            const body = typeof ctx === 'string' ? JSON.parse(ctx) : await ctx?.json?.();
            msg = body?.message || body?.error || msg;
          }
        } catch (_) {}
        toast({ title: "Erreur", description: msg, variant: "destructive" });
        return;
      }

      if (data?.success) {
        toast({ 
          title: "Retrait confirmé !",
          description: `Commande de ${data.order?.client?.full_name || 'client'} récupérée`
        });
        fetchOrders();
        setOtpDialog(false);
        setOtpCode('');
        setSelectedOrder(null);
        setScanMode('qr');
      } else {
        toast({ 
          title: "Code invalide", 
          description: data?.message || "Le code OTP n'est pas valide",
          variant: "destructive" 
        });
      }
    } catch (error: any) {
      toast({ 
        title: "Erreur", 
        description: error.message || "Impossible de valider le code",
        variant: "destructive" 
      });
    }
  };

  const handleQRScanSuccess = (orderId: string, otpCode: string) => {
    // Automatically validate the pickup with scanned data
    handleConfirmPickup(orderId, otpCode);
  };

  const handleDepositQRScanSuccess = async (orderId: string, otpCode: string) => {
    // Store the scanned data and ask for shelf location
    setScannedOrderId(orderId);
    setScannedOtpCode(otpCode);
    setDepositScanMode('shelf');
    toast({ 
      title: "QR scanné avec succès !",
      description: "Veuillez indiquer l'emplacement de stockage"
    });
  };

  const handleConfirmDepositWithShelf = async () => {
    if (!scannedOrderId || !shelfLocation) {
      toast({ 
        title: "Erreur",
        description: "Veuillez indiquer un emplacement de stockage",
        variant: "destructive"
      });
      return;
    }

    try {
      // First validate vendor QR code if we have OTP
      if (scannedOtpCode) {
        console.log('[RelayDashboard] Validating vendor QR code...');
        
        const { data, error } = await supabase.functions.invoke('validate-vendor-pickup-token', {
          body: { orderId: scannedOrderId, otpCode: scannedOtpCode }
        });

        if (error) {
          console.error('[RelayDashboard] Error validating vendor token:', error);
          throw new Error(error.message || 'Erreur lors de la validation du QR code');
        }

        if (!data?.success) {
          toast({ 
            title: "Code invalide",
            description: data?.message || "Le code QR n'est pas valide",
            variant: "destructive"
          });
          return;
        }

        console.log('[RelayDashboard] Vendor QR code validated successfully');
        
        // Update shelf location only after successful validation
        const { error: updateError } = await supabase
          .from('orders')
          .update({ shelf_location: shelfLocation })
          .eq('id', scannedOrderId);

        if (updateError) {
          console.error('[RelayDashboard] Error updating shelf location:', updateError);
          throw updateError;
        }

        toast({ 
          title: "Dépôt confirmé !",
          description: `Commande stockée à l'emplacement ${shelfLocation}. Email envoyé au client.`,
          duration: 5000
        });
      } else {
        // If no OTP code, just update shelf location
        const { error: updateError } = await supabase
          .from('orders')
          .update({ shelf_location: shelfLocation })
          .eq('id', scannedOrderId);

        if (updateError) throw updateError;

        toast({ 
          title: "Emplacement enregistré",
          description: `Commande stockée à l'emplacement ${shelfLocation}`
        });
      }
      
      fetchOrders();
      setScanDialog(false);
      setDepositScanMode('qr');
      setScannedOrderId('');
      setScannedOtpCode('');
      setShelfLocation('');
    } catch (error: any) {
      console.error("[RelayDashboard] Error confirming deposit:", error);
      toast({ 
        title: "Erreur",
        description: error.message || "Impossible de confirmer le dépôt",
        variant: "destructive"
      });
    }
  };

  const handleScanClientQR = (order: Order) => {
    setSelectedOrder(order);
    setShowClientScanner(true);
  };

  const handleClientScanSuccess = () => {
    setShowClientScanner(false);
    setSelectedOrder(null);
    fetchOrders();
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'PAID': { variant: 'secondary' as const, label: 'En attente de dépôt' },
      'AVAILABLE_FOR_PICKUP': { variant: 'default' as const, label: 'Disponible' },
      'PICKED_UP': { variant: 'outline' as const, label: 'Récupéré' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || { variant: 'secondary' as const, label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const awaitingDropOff = orders.filter(o => o.status === 'PAID');
  const availableForPickup = orders.filter(o => o.status === 'AVAILABLE_FOR_PICKUP');
  const pickedUp = orders.filter(o => o.status === 'PICKED_UP');
  
  // Stats
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const pickedUpToday = pickedUp.filter(o => 
    o.created_at && new Date(o.created_at) >= today
  ).length;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container py-8">
        <div className="max-w-6xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold">Point Relais - Interface</h1>
              <p className="text-muted-foreground">
                Gérez les dépôts et retraits de colis
              </p>
            </div>
            <Button variant="outline" onClick={signOut}>
              <LogOut className="h-4 w-4 mr-2" />
              Déconnexion
            </Button>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setScanDialog(true)}>
              <CardHeader className="text-center">
                <div className="mx-auto w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center mb-4">
                  <QrCode className="h-6 w-6 text-primary-foreground" />
                </div>
                <CardTitle>Nouveau Dépôt</CardTitle>
                <CardDescription>
                  Scannez le code QR du vendeur pour confirmer un dépôt
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => {
              if (availableForPickup.length > 0) {
                setSelectedOrder(null);
                setScanMode('qr');
                setOtpDialog(true);
              } else {
                toast({
                  title: "Aucun colis disponible",
                  description: "Il n'y a pas de colis prêt pour le retrait",
                  variant: "destructive"
                });
              }
            }}>
              <CardHeader className="text-center">
                <div className="mx-auto w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center mb-4">
                  <Scan className="h-6 w-6 text-primary-foreground" />
                </div>
                <CardTitle>Retrait Client</CardTitle>
                <CardDescription>
                  Scannez le QR ou saisissez le code OTP pour un retrait
                </CardDescription>
              </CardHeader>
            </Card>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">En attente de dépôt</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{awaitingDropOff.length}</div>
                <p className="text-xs text-muted-foreground">Commandes payées</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Disponible retrait</CardTitle>
                <Package className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{availableForPickup.length}</div>
                <p className="text-xs text-muted-foreground">Prêt à être récupéré</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Récupérées aujourd'hui</CardTitle>
                <CheckCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{pickedUpToday}</div>
                <p className="text-xs text-muted-foreground">Aujourd'hui</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total commandes</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{orders.length}</div>
                <p className="text-xs text-muted-foreground">Toutes les commandes</p>
              </CardContent>
            </Card>
          </div>

          {/* Orders Management */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="awaiting">En attente ({awaitingDropOff.length})</TabsTrigger>
              <TabsTrigger value="available">Disponibles ({availableForPickup.length})</TabsTrigger>
              <TabsTrigger value="completed">Récupérées ({pickedUp.length})</TabsTrigger>
            </TabsList>

            {/* Awaiting Drop Off */}
            <TabsContent value="awaiting" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Commandes en attente de dépôt</CardTitle>
                  <CardDescription>
                    Ces commandes doivent être déposées par les vendeurs
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {awaitingDropOff.length === 0 ? (
                    <div className="text-center py-8">
                      <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">Aucune commande en attente</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {awaitingDropOff.map((order) => (
                        <Card key={order.id} className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex space-x-4">
                              {order.product?.images?.[0] && (
                                <img
                                  src={order.product.images[0]}
                                  alt={order.product?.title}
                                  className="w-16 h-16 object-cover rounded-lg"
                                />
                              )}
                              <div className="space-y-1">
                                <h3 className="font-semibold">{order.product?.title}</h3>
                                <p className="text-sm text-muted-foreground">
                                  Client: {order.client.full_name || 'Non renseigné'}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  {new Date(order.created_at).toLocaleDateString('fr-FR')}
                                </p>
                              </div>
                            </div>
                            <div className="text-right space-y-2">
                              {getStatusBadge(order.status)}
                              <p className="font-semibold">
                                {formatPriceWithCurrency(order.amount_cents, order.currency, displayCurrency, convertPrice)}
                              </p>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Available for Pickup */}
            <TabsContent value="available" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Colis disponibles pour retrait</CardTitle>
                  <CardDescription>
                    Scannez le QR code du client pour confirmer le retrait
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {availableForPickup.length === 0 ? (
                    <div className="text-center py-8">
                      <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">Aucun colis disponible</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {availableForPickup.map((order) => (
                        <Card key={order.id} className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex space-x-4">
                              {order.product?.images?.[0] && (
                                <img
                                  src={order.product.images[0]}
                                  alt={order.product?.title}
                                  className="w-16 h-16 object-cover rounded-lg"
                                />
                              )}
                              <div className="space-y-1">
                                <h3 className="font-semibold">{order.product?.title}</h3>
                                <p className="text-sm text-muted-foreground">
                                  Client: {order.client.full_name || 'Non renseigné'}
                                </p>
                                <p className="text-sm text-muted-foreground flex items-center">
                                  <MapPin className="h-3 w-3 mr-1" />
                                  Emplacement: {order.shelf_location || 'Non assigné'}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {new Date(order.created_at).toLocaleDateString('fr-FR')}
                                </p>
                              </div>
                            </div>
                            <div className="text-right space-y-2">
                              {getStatusBadge(order.status)}
                              <p className="font-semibold">
                                {formatPriceWithCurrency(order.amount_cents, order.currency, displayCurrency, convertPrice)}
                              </p>
                              <Button 
                                size="sm" 
                                onClick={() => handleScanClientQR(order)}
                              >
                                <ScanLine className="h-3 w-3 mr-1" />
                                Scanner QR Client
                              </Button>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Picked Up Orders */}
            <TabsContent value="completed" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Commandes récupérées</CardTitle>
                  <CardDescription>
                    Historique des commandes retirées par les clients
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {pickedUp.length === 0 ? (
                    <div className="text-center py-8">
                      <CheckCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">Aucune commande récupérée</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {pickedUp.slice(0, 20).map((order) => (
                        <Card key={order.id} className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex space-x-4">
                              {order.product?.images?.[0] && (
                                <img
                                  src={order.product.images[0]}
                                  alt={order.product?.title}
                                  className="w-16 h-16 object-cover rounded-lg"
                                />
                              )}
                              <div className="space-y-1">
                                <h3 className="font-semibold">{order.product?.title}</h3>
                                <p className="text-sm text-muted-foreground">
                                  Client: {order.client.full_name || 'Non renseigné'}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Récupéré le: {new Date(order.created_at).toLocaleDateString('fr-FR')}
                                </p>
                              </div>
                            </div>
                            <div className="text-right space-y-2">
                              {getStatusBadge(order.status)}
                              <p className="font-semibold">
                                {formatPriceWithCurrency(order.amount_cents, order.currency, displayCurrency, convertPrice)}
                              </p>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Dialogs */}
          {/* Deposit Dialog */}
          <Dialog open={scanDialog} onOpenChange={(open) => {
            setScanDialog(open);
            if (!open) {
              setDepositScanMode('qr');
              setScannedOrderId('');
              setScannedOtpCode('');
              setSelectedDepositOrderId('');
              setShelfLocation('');
            }
          }}>
            <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Confirmer le dépôt vendeur</DialogTitle>
                <DialogDescription>
                  {depositScanMode === 'qr' 
                    ? "Scannez le code QR du vendeur ou utilisez la saisie manuelle" 
                    : depositScanMode === 'select'
                    ? "Sélectionnez la commande à confirmer"
                    : depositScanMode === 'shelf'
                    ? "QR code validé ! Assignez un emplacement de stockage"
                    : "Assignez un emplacement de stockage"}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {depositScanMode === 'qr' ? (
                  <div>
                    <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-4 sm:p-8">
                      <QRScanner
                        onScanSuccess={handleDepositQRScanSuccess}
                        onClose={() => setDepositScanMode('select')}
                      />
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 mt-4">
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => setDepositScanMode('select')}
                      >
                        Saisie manuelle
                      </Button>
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => setScanDialog(false)}
                      >
                        Annuler
                      </Button>
                    </div>
                  </div>
                ) : depositScanMode === 'select' ? (
                  <div className="space-y-4">
                    <div>
                      <Label>Sélectionner la commande</Label>
                      {awaitingDropOff.length === 0 ? (
                        <p className="text-sm text-muted-foreground mt-2">
                          Aucune commande en attente de dépôt
                        </p>
                      ) : (
                        <div className="mt-2 space-y-2 max-h-60 overflow-y-auto">
                          {awaitingDropOff.map((order) => (
                            <div 
                              key={order.id}
                              className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                                selectedDepositOrderId === order.id 
                                  ? 'border-primary bg-primary/5' 
                                  : 'border-muted hover:border-primary/50'
                              }`}
                              onClick={() => setSelectedDepositOrderId(order.id)}
                            >
                              <div className="flex items-center gap-3">
                                {order.product?.images?.[0] && (
                                  <img 
                                    src={order.product.images[0]} 
                                    alt="" 
                                    className="w-12 h-12 object-cover rounded"
                                  />
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm truncate">{order.product?.title}</p>
                                  <p className="text-xs text-muted-foreground">
                                    Client: {order.client.full_name || 'Non renseigné'}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    ID: {order.id.substring(0, 8)}...
                                  </p>
                                </div>
                                {selectedDepositOrderId === order.id && (
                                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => {
                          setDepositScanMode('qr');
                          setSelectedDepositOrderId('');
                        }}
                      >
                        Retour au scan
                      </Button>
                      <Button 
                        className="flex-1"
                        onClick={() => {
                          if (selectedDepositOrderId) {
                            setScannedOrderId(selectedDepositOrderId);
                            setDepositScanMode('manual');
                          }
                        }}
                        disabled={!selectedDepositOrderId}
                      >
                        Continuer
                      </Button>
                    </div>
                  </div>
                ) : depositScanMode === 'shelf' || depositScanMode === 'manual' ? (
                  <>
                    {scannedOrderId && (
                      <div className={`p-3 rounded-lg ${depositScanMode === 'shelf' ? 'bg-green-50 border border-green-200' : 'bg-muted'}`}>
                        {depositScanMode === 'shelf' && (
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <p className="text-sm font-medium text-green-700">QR Code validé</p>
                          </div>
                        )}
                        <p className="text-sm font-medium">Commande sélectionnée</p>
                        <p className="text-xs text-muted-foreground">ID: {scannedOrderId.substring(0, 8)}...</p>
                        {awaitingDropOff.find(o => o.id === scannedOrderId) && (
                          <p className="text-xs font-medium mt-1">
                            {awaitingDropOff.find(o => o.id === scannedOrderId)?.product.title}
                          </p>
                        )}
                      </div>
                    )}
                    <div>
                      <Label htmlFor="shelfLocation">Emplacement de stockage *</Label>
                      <Input
                        id="shelfLocation"
                        placeholder="Ex: A1, B5, C3..."
                        value={shelfLocation}
                        onChange={(e) => setShelfLocation(e.target.value.toUpperCase())}
                        autoFocus
                        className="mt-2"
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        Indiquez où le colis sera stocké pour faciliter sa recherche lors du retrait
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => {
                          if (depositScanMode === 'shelf') {
                            setDepositScanMode('qr');
                            setScannedOtpCode('');
                          } else {
                            setDepositScanMode('select');
                          }
                          setScannedOrderId('');
                          setShelfLocation('');
                        }}
                      >
                        Retour
                      </Button>
                      <Button 
                        className="flex-1"
                        onClick={() => {
                          if (depositScanMode === 'shelf') {
                            handleConfirmDepositWithShelf();
                          } else if (scannedOrderId && shelfLocation) {
                            handleConfirmDropOff(scannedOrderId, shelfLocation);
                          }
                        }}
                        disabled={!shelfLocation || !scannedOrderId}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Confirmer le dépôt
                      </Button>
                    </div>
                  </>
                ) : null}
              </div>
            </DialogContent>
          </Dialog>

          {/* OTP Pickup Dialog */}
          <Dialog open={otpDialog} onOpenChange={(open) => {
            setOtpDialog(open);
            if (!open) {
              setOtpCode('');
              setSelectedOrder(null);
              setScanMode('qr');
            }
          }}>
            <DialogContent className="w-[95vw] max-w-lg">
              <DialogHeader>
                <DialogTitle>Retrait Client</DialogTitle>
                <DialogDescription>
                  {scanMode === 'qr' 
                    ? "Scannez le QR code du client ou saisissez le code OTP manuellement" 
                    : "Saisissez le code OTP à 6 chiffres"}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {scanMode === 'qr' ? (
                  <div>
                    <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-4 sm:p-8">
                      <QRScanner
                        onScanSuccess={handleQRScanSuccess}
                        onClose={() => setScanMode('manual')}
                      />
                    </div>
                    <Button 
                      variant="outline" 
                      className="w-full mt-4"
                      onClick={() => setScanMode('manual')}
                    >
                      Saisir le code manuellement
                    </Button>
                  </div>
                ) : (
                  <>
                    <div>
                      <Label>Commande à remettre</Label>
                      {availableForPickup.length === 0 ? (
                        <p className="text-sm text-muted-foreground mt-2">
                          Aucun colis disponible pour le retrait
                        </p>
                      ) : (
                        <div className="mt-2 space-y-2 max-h-52 overflow-y-auto">
                          {availableForPickup.map((order) => (
                            <div
                              key={order.id}
                              className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                                selectedOrder?.id === order.id
                                  ? 'border-primary bg-primary/5'
                                  : 'border-muted hover:border-primary/50'
                              }`}
                              onClick={() => setSelectedOrder(order)}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <p className="text-sm font-medium">{order.product.title}</p>
                                  <p className="text-xs text-muted-foreground">
                                    ID: {order.id.substring(0, 8)}...
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Client: {order.client.full_name || 'Non renseigné'}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    Emplacement: {order.shelf_location || 'Non assigné'}
                                  </p>
                                </div>
                                <div className="text-right">
                                  {getStatusBadge(order.status)}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {selectedOrder && (
                      <div className="p-3 rounded-lg bg-muted">
                        <p className="text-sm font-medium">Commande sélectionnée</p>
                        <p className="text-xs text-muted-foreground">ID: {selectedOrder.id.substring(0, 8)}...</p>
                        <p className="text-xs font-medium mt-1">{selectedOrder.product.title}</p>
                      </div>
                    )}
                    <div>
                      <Label htmlFor="otpCode">Code OTP (6 chiffres)</Label>
                      <Input
                        id="otpCode"
                        type="text"
                        inputMode="numeric"
                        placeholder="123456"
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').substring(0, 6))}
                        maxLength={6}
                        autoFocus
                        className="mt-2 text-center text-2xl tracking-widest"
                      />
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => {
                          setScanMode('qr');
                          setOtpCode('');
                        }}
                      >
                        Retour au scan
                      </Button>
                      <Button 
                        className="flex-1"
                        onClick={() => {
                          if (selectedOrder && otpCode.length === 6) {
                            handleConfirmPickup(selectedOrder.id, otpCode);
                          }
                        }}
                        disabled={!otpCode || otpCode.length !== 6 || !selectedOrder}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Confirmer le retrait
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Client QR Scanner */}
          <ClientQRScanner
            open={showClientScanner}
            onClose={() => setShowClientScanner(false)}
            onSuccess={handleClientScanSuccess}
            orderId={selectedOrder?.id || ''}
          />
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default RelayDashboard;